import type { ReactNode } from 'react';
import type { AddressInfoType } from './inspector';


export interface LatLngLiteral {
  lat: number;
  lng: number;
}


export type MarkerIcon =
  | { url: string; size?: { width: number; height: number }; anchor?: { x: number; y: number } }
  | 'default'
  | string;


export type MarkerAnimation = 'drop' | 'bounce' | null;


export type GestureHandling = 'greedy' | 'cooperative' | 'auto' | 'none';


export interface MapMarker {
  position: LatLngLiteral;
  title?: string;
  draggable?: boolean;
  id?: string | number;
  icon?: MarkerIcon;
  animation?: MarkerAnimation;
  onClick?: () => void;
  onDragEnd?: (position: LatLngLiteral) => void;
}


export interface MapOptions {
  center: LatLngLiteral;
  zoom: number;
  disableDefaultUI?: boolean;
  gestureHandling?: GestureHandling;
  styles?: any[];
  controls?: {
    fullscreen?: boolean;
    streetView?: boolean;
    mapType?: boolean;
    scale?: boolean;
    zoom?: boolean;
  };
}


export interface GeocodingResult {
  location: LatLngLiteral;
  formattedAddress: string;
  addressInfo: AddressInfoType;
  placeId?: string;
}


export interface AddressComponent {
  types: string[];
  shortName: string;
  longName: string;
}


export interface AutocompleteResult {
  placeId: string;
  description: string;
  formattedAddress?: string;
  geometry?: {
    location: LatLngLiteral;
  };
  addressComponents?: AddressComponent[];
}


export interface MapBounds {
  extend(position: LatLngLiteral): void;
  isEmpty(): boolean;
  getNorthEast(): LatLngLiteral;
  getSouthWest(): LatLngLiteral;
}


export interface MapInstance {
  setCenter(position: LatLngLiteral): void;
  getCenter(): LatLngLiteral | null;
  setZoom(zoom: number): void;
  getZoom(): number | null;
  panTo(position: LatLngLiteral): void;
  fitBounds(bounds: MapBounds, padding?: number): void;
  getBounds(): MapBounds | null;
  addListener(event: string, callback: (...args: any[]) => void): void;
  removeListener(listener: any): void;
}


export interface MarkerInstance {
  setPosition(position: LatLngLiteral): void;
  getPosition(): LatLngLiteral | null;
  setDraggable(draggable: boolean): void;
  setTitle(title: string): void;
  setIcon(icon: MarkerIcon): void;
  setAnimation(animation: MarkerAnimation): void;
  setVisible(visible: boolean): void;
  addListener(event: string, callback: (...args: any[]) => void): void;
  removeListener(listener: any): void;
  destroy(): void;
}


export interface MapProviderProps {
  apiKey?: string;
  children?: ReactNode;
}

export interface MapComponentProps {
  id?: string;
  options: MapOptions;
  onMapReady?: (mapInstance: MapInstance) => void;
  onIdle?: (mapInstance: MapInstance) => void;
  onClick?: (position: LatLngLiteral) => void;
  className?: string;
  style?: React.CSSProperties;
}

export interface AutocompleteProps {
  value?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  onPlaceSelect?: (result: AutocompleteResult | null) => void;
  className?: string;
  disabled?: boolean;
}

export interface InfoWindowProps {
  position: LatLngLiteral;
  content?: string;
  onClose?: () => void;
  children?: ReactNode;
}


export interface MapProvider {
  
  readonly name: string;
  readonly version: string;
  
  
  loadScript(apiKey: string): Promise<void>;
  isScriptLoaded(): boolean;
  
  
  createAPIProvider(props: MapProviderProps): JSX.Element;
  createMap(props: MapComponentProps): JSX.Element;
  createAutocomplete(props: AutocompleteProps): JSX.Element;
  createInfoWindow(props: InfoWindowProps): JSX.Element;
  
  
  createMapInstance(element: HTMLElement, options: MapOptions): Promise<MapInstance>;
  createMarker(options: {
    map: MapInstance;
    position: LatLngLiteral;
    draggable?: boolean;
    title?: string;
    icon?: MarkerIcon;
    animation?: MarkerAnimation;
  }): MarkerInstance;
  createBounds(): MapBounds;
  
  
  geocodeAddress(address: string): Promise<GeocodingResult | null>;
  reverseGeocode(position: LatLngLiteral): Promise<GeocodingResult | null>;
  getPlacePredictions(input: string): Promise<AutocompleteResult[]>;
  getPlaceDetails(placeId: string): Promise<AutocompleteResult | null>;
  
  
  parseAddressComponents(components: AddressComponent[]): AddressInfoType;
  
  
  destroy(): void;
}


export type MapProviderType = 'google' | 'baidu' | 'openstreetmap';


export interface MapProviderFactory {
  createProvider(type: MapProviderType, apiKey?: string): Promise<MapProvider>;
  isProviderSupported(type: MapProviderType): boolean;
  getAvailableProviders(): MapProviderType[];
}


export class MapProviderError extends Error {
  constructor(
    message: string,
    public readonly provider?: string,
    public readonly code?: string
  ) {
    super(message);
    this.name = 'MapProviderError';
  }
}

export class MapProviderLoadError extends MapProviderError {
  constructor(provider: string, originalError?: Error) {
    console.log('MapProviderLoadError', provider, originalError);
    super(`Failed to load ${provider} maps provider`, provider, 'LOAD_ERROR');
    if (originalError) {
      this.stack = originalError.stack;
    }
  }
}

export class MapProviderNotSupportedError extends MapProviderError {
  constructor(provider: string) {
    super(`Map provider '${provider}' is not supported`, provider, 'NOT_SUPPORTED');
  }
}


